import { useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { CONTACT_EMAIL, TWITTER_X_URL, LINKEDIN_URL } from "../config";
import { FaEnvelope, FaLinkedin, FaPaperPlane } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

// Same URL resolution as the other API modules (see src/api/*.js)
const isDevelopment = process.env.NODE_ENV === "development";
const API_URL = isDevelopment ? "http://localhost:3002/api" : "/api";

function ContactForm() {
  const [formState, setFormState] = useState({
    email: "",
    firstName: "",
    lastName: "",
    comments: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formState),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage("Your message has been sent successfully!");
        setFormState({ email: "", firstName: "", lastName: "", comments: "" });
      } else {
        throw new Error(data.message || "Something went wrong");
      }
    } catch (error) {
      setSuccessMessage("There was an error submitting your message.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="contact-form"
      id="contact-form"
    >
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 p-4 rounded-lg text-center ${
            successMessage.includes("error")
              ? "bg-red-100 text-red-700"
              : "bg-green-100 text-green-700"
          }`}
        >
          {successMessage}
        </motion.div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mx-auto mt-8 max-w-xl bg-white/80 dark:bg-brand-dark/80 backdrop-blur-md shadow-2xl rounded-2xl p-8 border border-gray-200 dark:border-brand-purple/20"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          <div>
            <label
              htmlFor="first-name"
              className="block text-sm font-semibold leading-6 text-brand-gold"
            >
              First name
            </label>
            <div className="mt-2.5">
              <motion.input
                type="text"
                name="firstName"
                id="first-name"
                required
                autoComplete="given-name"
                value={formState.firstName}
                onChange={handleChange}
                className="block w-full rounded-lg border-0 px-4 py-3 text-gray-800 dark:text-brand-cream bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-brand-purple/30 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-gold sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-brand-gold/50"
                whileFocus={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="last-name"
              className="block text-sm font-semibold leading-6 text-brand-gold"
            >
              Last name
            </label>
            <div className="mt-2.5">
              <motion.input
                type="text"
                name="lastName"
                id="last-name"
                required
                autoComplete="family-name"
                value={formState.lastName}
                onChange={handleChange}
                className="block w-full rounded-lg border-0 px-4 py-3 text-gray-800 dark:text-brand-cream bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-brand-purple/30 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-gold sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-brand-gold/50"
                whileFocus={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label
              htmlFor="email"
              className="block text-sm font-semibold leading-6 text-brand-gold"
            >
              Email
            </label>
            <div className="mt-2.5">
              <motion.input
                type="email"
                name="email"
                id="email"
                required
                autoComplete="email"
                value={formState.email}
                onChange={handleChange}
                className="block w-full rounded-lg border-0 px-4 py-3 text-gray-800 dark:text-brand-cream bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-brand-purple/30 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-gold sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-brand-gold/50"
                whileFocus={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label
              htmlFor="comments"
              className="block text-sm font-semibold leading-6 text-brand-gold"
            >
              Message
            </label>
            <div className="mt-2.5">
              <motion.textarea
                name="comments"
                id="comments"
                required
                rows="4"
                value={formState.comments}
                onChange={handleChange}
                placeholder="How can I help you?"
                className="block w-full rounded-lg border-0 px-4 py-3 text-gray-800 dark:text-brand-cream bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-brand-purple/30 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-gold sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-brand-gold/50 resize-none"
                whileFocus={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </div>
          </div>
        </div>

        <div className="mt-10">
          <button
            type="submit"
            disabled={isSubmitting}
            className="group relative w-full rounded-md bg-gradient-to-r from-brand-gold to-brand-purple px-3.5 py-2.5 text-center text-sm font-semibold text-[#f3eacc] shadow-sm hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold transition-all duration-200 disabled:opacity-70"
          >
            {isSubmitting ? (
              "Sending..."
            ) : (
              <>
                <FaPaperPlane className="inline-block mr-2 group-hover:translate-x-1 transition-transform" />
                Send Message
              </>
            )}
          </button>
        </div>
      </form>

      {/* Social Links Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="contact-methods py-20"
      >
        <h2 className="text-4xl mb-8 font-semibold text-center text-gradient">
          Connect With Me
        </h2>

        <div className="flex justify-center space-x-8">
          <motion.a
            href={`mailto:${CONTACT_EMAIL}`}
            className="contact-link group flex flex-col items-center"
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="p-4 rounded-full bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-300 dark:border-brand-purple/30 group-hover:border-brand-gold group-hover:shadow-glow transition-all duration-300"
              whileHover={{ scale: 1.1 }}
            >
              <FaEnvelope className="text-2xl text-brand-gold group-hover:text-brand-gold transition-colors duration-300" />
            </motion.div>
            <span className="mt-3 text-gray-700 dark:text-brand-cream group-hover:text-brand-gold transition-colors duration-300 font-medium">
              Email
            </span>
          </motion.a>

          <motion.a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="contact-link group flex flex-col items-center"
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="p-4 rounded-full bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-300 dark:border-brand-purple/30 group-hover:border-brand-gold group-hover:shadow-glow transition-all duration-300"
              whileHover={{ scale: 1.1 }}
            >
              <FaLinkedin className="text-2xl text-brand-gold group-hover:text-brand-gold transition-colors duration-300" />
            </motion.div>
            <span className="mt-3 text-gray-700 dark:text-brand-cream group-hover:text-brand-gold transition-colors duration-300 font-medium">
              LinkedIn
            </span>
          </motion.a>

          <motion.a
            href={TWITTER_X_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="contact-link group flex flex-col items-center"
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="p-4 rounded-full bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-300 dark:border-brand-purple/30 group-hover:border-brand-gold group-hover:shadow-glow transition-all duration-300"
              whileHover={{ scale: 1.1 }}
            >
              <FaXTwitter className="text-2xl text-brand-gold group-hover:text-brand-gold transition-colors duration-300" />
            </motion.div>
            <span className="mt-3 text-gray-700 dark:text-brand-cream group-hover:text-brand-gold transition-colors duration-300 font-medium">
              X
            </span>
          </motion.a>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Contact() {
  return (
    <div className="min-h-screen text-gray-800 dark:text-brand-cream">
      <Helmet>
        <title>Contact - David Seibold</title>
        <meta
          name="description"
          content="Get in touch with David Seibold — full-stack DeFi engineer open to projects, collaborations, and opportunities."
        />
        <meta property="og:title" content="Contact - David Seibold" />
        <meta
          property="og:description"
          content="Reach out via the contact form, email, LinkedIn, or X."
        />
        <meta
          property="og:image"
          content="https://daveyrockets.me/og-banner.png"
        />
        <meta property="og:url" content="https://daveyrockets.me/contact" />
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto px-4 py-12"
      >
        <h1 className="heading-hero text-center text-gradient mb-8">
          Get in Touch
        </h1>

        <p className="text-gray-700 dark:text-brand-cream text-center max-w-2xl mx-auto mb-12 text-lg">
          Please feel free to reach out by whatever means you prefer. All my
          links are below, otherwise fill out the form and I'll reach out to
          you!
        </p>

        <ContactForm />
      </motion.div>
    </div>
  );
}

export default Contact;
